/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useStores } from 'hooks'
import { useCallback, useEffect, useState } from 'react'
import { ChevronDown as RiArrowDownSLine } from 'components/icons'
import { ChevronUp as RiArrowUpSLine, Lock01 as RiLock2Line, DotsVertical as RiMore2Fill } from 'components/icons'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'

import { GButton, GConfirm, GDropdown, GLoading } from 'components/basic-blocks'

import { VisualPreferencesItem } from 'api-models'
import { Rules } from 'services/user/rules'
import { getPermissionTier } from 'utils'
import { truncateString } from '../../../../../../../../utils/text'
import { RuleForm } from '../rule-form'
import { RuleFormType } from '../rule-form/types'
import { RuleSummary } from '../rule-summary'
import { RulesListProps } from './types'
import { mapFetchedRules, prepareQuery } from './utils'
import { GNewToggle } from '../../../../../../../../components/basic-blocks/g-new-toggle/g-new-toggle'
import { Bell01 as IoMdAlarm } from 'components/icons'
const ContextMenuIcon = () => {
	return (
		<div className="flex items-center cursor-pointer rounded-md hover:bg-gray-50 h-6">
			<RiMore2Fill className="h-4 w-4 text-color-text-default" />
		</div>
	)
}

const showSummary = (
	rulesVisualPreferences: VisualPreferencesItem['rules'],
	ruleId?: string
) => {
	if (!rulesVisualPreferences || rulesVisualPreferences.length === 0)
		return false

	const ruleVisual = rulesVisualPreferences.find((r: any) => r.id === ruleId)

	if (!ruleVisual) return false

	return ruleVisual.show
}

export const RulesList = ({
	level,
	targetId,
	targetName,
	platform,
	activeTab
}: RulesListProps) => {
	const { accountStore, assetStore, userStore } = useStores()
	const { account } = accountStore
	const { asset } = assetStore
	const { visualPreferences, updateVisualPreferences } = userStore

	const [rules, setRules] = useState<RuleFormType[] | undefined>([])
	const [deleteAllRulesLoading, setDeleteAllRules] = useState(false)
	const [deleteRuleLoading, setDeleteRuleLoading] = useState(false)

	const [rulesVisualPreferences, setRuleVisualPreferences] = useState<
		VisualPreferencesItem['rules']
	>([])

	const hasAccess =
		getPermissionTier(account?.billing?.subscription?.plan.tier) !== 'lite'

	useEffect(() => {
		if (visualPreferences) {
			const { rules: rulesVisuals } =
				JSON.parse(visualPreferences).find(
					(v: VisualPreferencesItem) =>
						v.target_id === asset?.id && v.page === 'protection'
				) || {}

			setRuleVisualPreferences(rulesVisuals)
		}
	}, [])

	const cacheKey = () => {
		return ['rules-panel', asset?.account_id, asset?.id, targetId]
	}
	const query = prepareQuery(level, asset?.id, targetId)
	const { isLoading, refetch } = useQuery(
		cacheKey(),
		() => Rules.listAllRules({ accountId: asset?.account_id, query }),
		{
			select: mapFetchedRules,
			onSettled: (data) => setRules(data)
		}
	)

	const handleToggleRuleStatus = async (id: string, enabled: boolean) => {
		try {
			setRules((prev) => {
				return prev?.map((r) => {
					if (r.id === id) {
						r.enabled = !enabled
						return r
					}

					return r
				})
			})

			await Rules.toggleRuleStatus({
				accountId: asset?.account_id,
				id,
				enabled: !enabled
			})
		} catch (e) {
			toast.error('Failed to toggle rule status')
			return
		}
	}

	const handleDeleteAllRules = async (onClose: (() => void) | undefined) => {
		try {
			setDeleteAllRules(true)

			await Rules.deleteAllRules({
				accountId: asset?.account_id,
				query
			})

			await refetch()

			if (onClose) onClose()

			toast.success('Rules deleted successfully')
		} catch (e) {
			toast.error('Failed to delete all rules')
			return
		} finally {
			setDeleteAllRules(false)
		}
	}

	const handleDeleteRule = async (ruleId?: string, onClose?: () => void) => {
		try {
			setDeleteRuleLoading(true)

			await Rules.deleteRule({
				accountId: asset?.account_id,
				ruleId
			})

			await refetch()

			if (onClose) onClose()

			toast.success('Rule deleted successfully')
		} catch (e) {
			toast.error('Failed to delete rule')
			return
		} finally {
			setDeleteRuleLoading(false)
		}
	}

	useEffect(() => {
		refetch()
	}, [refetch])

	const handleToggleRuleSummary = useCallback(
		(ruleId: string) => {
			const preferences = visualPreferences
				? (JSON.parse(visualPreferences) as VisualPreferencesItem[])
				: []

			if (!rulesVisualPreferences) {
				preferences.push({
					target_id: asset?.id!,
					page: 'protection',
					rules: [{ id: ruleId, show: false }]
				})
				setRuleVisualPreferences([{ id: ruleId, show: false }])
				updateVisualPreferences(JSON.stringify(preferences))
				return
			}

			const copyState = [...rulesVisualPreferences]
			const visualIndex = copyState.findIndex((p) => p.id === ruleId)
			if (visualIndex === -1) {
				copyState.push({ id: ruleId, show: false })
			} else {
				const show = copyState[visualIndex].show
				copyState[visualIndex] = {
					id: ruleId,
					show: !show
				}
			}

			const pageIndex = preferences.findIndex(
				(p) => p.target_id === asset?.id && p.page === 'protection'
			)
			if (pageIndex === -1) {
				preferences.push({
					target_id: asset?.id!,
					page: 'protection',
					rules: copyState
				})
			} else {
				preferences[pageIndex] = {
					...preferences[pageIndex],
					rules: copyState
				}
			}

			setRuleVisualPreferences(copyState)
			updateVisualPreferences(JSON.stringify(preferences))
		},
		[rulesVisualPreferences]
	)

	if (isLoading) return <GLoading />

	return (
		<div>
			<header className="pb-4">
				<div className="flex align-middle justify-between">
					<span className="font-medium text-t-color-default text-xl">
						Custom rules
					</span>
					{hasAccess && (
						<div className="flex">
							<RuleForm
								level={level}
								refetch={refetch}
								targetId={targetId}
								targetName={targetName}
								platform={platform}
							>
								{(open) => (
									<GButton
										label="Add rule"
										variant="contained"
										color="yellow"
										labelClassName="font-medium text-base"
										className="h-[30px] w-max rounded-full disabled:!shadow-none !shadow-s-yellow"
										onClick={() => open()}
									/>
								)}
							</RuleForm>
						</div>
					)}
				</div>
				<span className="text-t-checkbox-unactive font-md max-w-[350px] block leading-5">
					Take full control over your traffic with custom protection rules
				</span>
			</header>
			<main>
				{!hasAccess ? (
					<div className="flex flex-col items-center justify-center">
						<RiLock2Line className="h-14 w-14 text-t-menu" />
						<strong className="text-t-secondary my-4 font-bold">
							You don't have access to Custom Rules
						</strong>
						<span className="text-t-secondary max-w-[250px] text-center mb-4">
							Upgrade your plan to Standard or Pro tier to have access to this
							feature
						</span>
						<GButton
							color="primary"
							variant="contained"
							size="sm"
							onClick={() =>
								window.open(`/settings/workspace/${account?.id}/billing`)
							}
						>
							Upgrade your plan
						</GButton>
					</div>
				) : (
					<>
						{rules && rules.length === 0 ? (
							<div className="flex flex-col items-center justify-center">
								<IoMdAlarm className="h-14 w-14 text-t-base-blue" />
								<strong className="text-t-base-blue my-4 font-medium">
									You don’t have custom rules for{' '}
									{truncateString(targetName || '', 20)}
								</strong>
								<span className="text-t-checkbox-unactive pb-4 max-w-[400px] text-center">
									Custom protection rules allow you to deal with fraud with
									surgical precision. No one knows your audience better than you
									do.
								</span>
								<RuleForm
									level={level}
									refetch={refetch}
									targetId={targetId}
									targetName={targetName}
									platform={platform}
								>
									{(open) => (
										<GButton
											size="lg"
											label="Create a rule"
											labelClassName="font-medium"
											className="h-[38px] border-t-base-blue hover:bg-transparent text-color-text-default w-full rounded-full"
											color={'buttonPrimary'}
											variant={'text'}
											onClick={() => open()}
										/>
									)}
								</RuleForm>
							</div>
						) : (
							<div className="bg-white rounded-b-lg">
								{rules?.map((rule, index) => {
									const isExpanded = showSummary(
										rulesVisualPreferences,
										rule.id
									)
									return (
										<div key={rule.id}>
											<div className="px-4 py-3">
												<div className="flex flex-row items-center">
													<GNewToggle
														checked={rule.enabled}
														onChange={() =>
															handleToggleRuleStatus(rule.id!, rule.enabled)
														}
														activeColor="success"
													/>
													<div className="flex flex-col ml-4 flex-1">
														<strong className="font-normal text-color-text-default">
															{rule?.name}
														</strong>
													</div>
													<div className="flex items-center gap-3">
														<div
															className="flex items-center cursor-pointer h-6"
															onClick={() => handleToggleRuleSummary(rule.id!)}
														>
															{isExpanded ? (
																<RiArrowUpSLine className="h-4 w-4 text-color-text-default" />
															) : (
																<RiArrowDownSLine className="h-4 w-4 text-color-text-default" />
															)}
														</div>
														<RuleForm
															rule={rule}
															refetch={refetch}
															level={level}
															targetId={targetId}
															targetName={targetName}
															platform={platform}
															isEditing={true}
															handleDeleteRule={handleDeleteRule}
														>
															{(open) => (
																<button
																	onClick={() => open()}
																	className="text-t-email-blue font-normal text-sm hover:underline cursor-pointer"
																>
																	Edit
																</button>
															)}
														</RuleForm>
														<div className="cursor-pointer">
															<GDropdown
																anchor="left"
																menuButton={<ContextMenuIcon />}
															>
																{(close?: () => void) => {
																	return (
																		<div className="z-50 border border-gray-200 rounded-md origin-top-right absolute mt-2 w-[100px] right-[-16px] text-sm bg-popover shadow-dropShadow divide-y font-bold ">
																			<div className="p-1 text-center hover:bg-popover-hover">
																				<RuleForm
																					rule={rule}
																					refetch={refetch}
																					level={level}
																					targetId={targetId}
																					targetName={targetName}
																					platform={platform}
																				>
																					{(open) => (
																						<span
																							className="font-medium"
																							onClick={() => open()}
																						>
																							Duplicate rule
																						</span>
																					)}
																				</RuleForm>
																			</div>
																			<div className="p-1 text-center hover:bg-popover-hover">
																				<GConfirm
																					type="danger"
																					title={`Delete rule ${truncateString(
																						rule?.name || '',
																						15
																					)}`}
																					description={`Are you sure you want to remove custom rule ${truncateString(
																						rule?.name || '',
																						15
																					)}? This action cannot be undone.`}
																					onConfirm={() =>
																						handleDeleteRule(rule.id!, close)
																					}
																					loading={deleteRuleLoading}
																					closeModalOnConfirm={true}
																				>
																					<span className="text-red-500 font-medium">
																						Delete rule
																					</span>
																				</GConfirm>
																			</div>
																		</div>
																	)
																}}
															</GDropdown>
														</div>
													</div>
												</div>
												{isExpanded && (
													<div className="ml-[56px] mt-2">
														<RuleSummary
															rule={rule}
															level={level}
															platform={platform}
															targetName={targetName}
														/>
													</div>
												)}
											</div>
											{index < (rules?.length || 0) - 1 && (
												<div className="border-b border-gray-200" />
											)}
										</div>
									)
								})}
							</div>
						)}
					</>
				)}
			</main>
		</div>
	)
}
